import { Item } from "components/atoms/Dropdown";

export interface MenuItem {
    id: string;
    displayValue: string;
    children?: MenuItem[];
    disabled?: boolean;
}

export interface Message {
    id: string;
    text: string;
    type: "send" | "receive";
    reasoningSteps?: string[];
    modelUsed?: string;
    showReasoningColumn?: boolean;
    isGenericResponse?: boolean;
    thoughts?: string;
    thinkingDuration?: number;
    exportRunUid?: string;
    agentId?: string;
    activeToolCalls?: string[];
    isThinking?: boolean;
    isFormattingResponse?: boolean;
}

export const buildingChatbotWelcomeMessage: Message = {
    id: crypto.randomUUID(),
    text: `<div>
        <p><strong>Hi! 👋 I'm your decarbonization assistant for this building.</strong></p>
        <p>Ask me about:</p>
        <ul>
            <li>Building features and energy performance</li>
            <li>Carbon reduction strategies</li>
            <li>Cost-effective upgrade measures</li>
            <li>Financial risks of inaction</li>
        </ul>
        <p>Or run an analysis from the AI menu.</p>
    </div>`,
    type: "receive",
};

export const portfolioChatbotWelcomeMessage: Message = {
    id: crypto.randomUUID(),
    text: `<div>
        <p><strong>Hi! 👋 I'm your portfolio decarbonization assistant.</strong></p>
        <p>Ask me about:</p>
        <ul>
            <li>Portfolio-wide carbon emissions and trends</li>
            <li>Buildings with the highest decarbonization potential</li>
            <li>Financial risk assessment across your portfolio</li>
            <li>Strategic decarbonization planning and priorities</li>
        </ul>
    </div>`,
    type: "receive",
};

interface AgentInfo {
    id: string;
    displayValue: string;
    disabled?: boolean;
    itemId?: string;
}

const agentDefinitions: Record<string, AgentInfo> = {
    generic: {
        id: "generic",
        displayValue: "building",
        itemId: "selenium-generic-menu-item",
    },
    finance: {
        id: "finance",
        displayValue: "market",
        itemId: "selenium-finance-menu-item",
    },
    compliance: {
        id: "compliance",
        displayValue: "compliance",
        itemId: "selenium-compliance-menu-item",
    },
    equipment_topology: {
        id: "equipment_topology",
        displayValue: "equipment",
        itemId: "selenium-equipmentTopology-menu-item",
    },
    incentive_capital: {
        id: "incentive_capital",
        displayValue: "CapEx reduction",
        itemId: "selenium-incentiveCapital-menu-item",
    },
    incentive_financing: {
        id: "incentive_financing",
        displayValue: "financial",
        itemId: "selenium-incentiveFinancing-menu-item",
    },
    incentive_tax: {
        id: "incentive_tax",
        displayValue: "tax",
        itemId: "selenium-incentiveTax-menu-item",
    },
    incentive_operational: {
        id: "incentive_operational",
        displayValue: "OpEx savings",
        itemId: "selenium-incentiveOperational-menu-item",
    },
    incentive_revenue: {
        id: "incentive_revenue",
        displayValue: "new revenue streams",
        itemId: "selenium-incentiveRevenue-menu-item",
    },
    measure_design: {
        id: "measure_design",
        displayValue: "Measure Design",
        itemId: "selenium-measureDesign-menu-item",
    },
};

const agentToItem = (agent: AgentInfo): Item => ({
    id: agent.id,
    displayValue: agent.displayValue,
    itemId: agent.itemId || `selenium-${agent.id}-menu-item`,
});

export const chatbotAgents: Item[] = [
    agentToItem(agentDefinitions.generic),
    agentToItem(agentDefinitions.finance),
    agentToItem(agentDefinitions.compliance),
    agentToItem(agentDefinitions.equipment_topology),
    agentToItem(agentDefinitions.incentive_capital),
    agentToItem(agentDefinitions.incentive_financing),
    agentToItem(agentDefinitions.incentive_tax),
    agentToItem(agentDefinitions.incentive_operational),
    agentToItem(agentDefinitions.incentive_revenue),
    agentToItem(agentDefinitions.measure_design),
];

export const MEASURE_DESIGN_PRIOR_AGENTS_DISCLAIMER =
    "For better results, run the Compliance and Incentives Analyses prior to measure design.";

export const GEMINI_INVALID_RESPONSE_USER_MESSAGE =
    "Sorry, Gemini did not return a valid response. Please run the agent again.";

export const chatbotWelcomeMessages = {
    building: buildingChatbotWelcomeMessage,
    portfolio: portfolioChatbotWelcomeMessage,
};

export const DOMPURIFY_ALLOWED_ATTRIBUTES = [
    "target",
    "rel",
    "href",
    "title",
    "class",
    "data-document-uid",
    "data-block-id",
    "data-document-index",
    "data-block-text",
    "data-page-number",
    "data-section-heading",
    "viewBox",
    "xmlns",
    "width",
    "height",
    "fill",
    "stroke",
    "stroke-width",
    "stroke-linecap",
    "stroke-linejoin",
];
